import GetAppIcon from "@mui/icons-material/GetApp";
import { Box, IconButton, Tooltip } from "@mui/material";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVDownload } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import {
  getCityWhitelabelMappings,
  getWhiteLabel,
} from "../../../actions/cities";
import {
  getRewardClaims,
  getRewardPrograms,
  getRewardRules,
} from "../../../actions/rewards";
import Tag from "../../../components/Tags";
import useCustomTable from "../../../components/table/CustomTable";
import { TAG_COLOR } from "../../../constants/const";
import { isSuperAdmin } from "../../../utils/utils";

const ClaimsContainer = () => {
  const dispatch = useDispatch();
  const [startDownload, setStartDownload] = useState(false);

  const adminDetails = useSelector((state) => state.auth.adminDetails);
  const rewardClaims = useSelector((state) => state.rewards.rewardClaims);
  const rewardClaimsSuccess = useSelector(
    (state) => state.rewards.rewardClaimsSuccess
  );
  const rewardRules = useSelector((state) => state.rewards.rewardRules);
  const rewardPrograms = useSelector((state) => state.rewards.rewardPrograms);
  const cityWhitelabelMapping = useSelector(
    (state) => state.cities.cityWhitelabelMapping
  );

  useEffect(() => {
    dispatch(getWhiteLabel());
    dispatch(getCityWhitelabelMappings());
    dispatch(getRewardRules({ page: 0, size: 1000 }));
    dispatch(getRewardPrograms({ page: 0, size: 1000 }));
  }, [dispatch]);

  const getRuleName = (ruleId) => {
    if (!rewardRules || !rewardRules.content) return `Rule ${ruleId}`;
    const rule = rewardRules.content.find((r) => r.id === ruleId);
    return rule ? rule.name : `Rule ${ruleId}`;
  };

  const filteredRewardClaims = useMemo(() => {
    if (!rewardClaims || !rewardClaims.content) return [];

    if (isSuperAdmin(adminDetails)) {
      return rewardClaims.content;
    }

    const adminWhitelabelId = adminDetails?.whiteLabelId;
    if (
      !adminWhitelabelId ||
      !cityWhitelabelMapping ||
      !rewardRules?.content ||
      !rewardPrograms?.content
    ) {
      return rewardClaims.content;
    }

    const whitelabel = cityWhitelabelMapping.find(
      (w) => w.id === adminWhitelabelId
    );
    if (!whitelabel || !whitelabel.paymentConfigurations) {
      return [];
    }

    const paymentConfigIds = whitelabel.paymentConfigurations.map(
      (pc) => pc.id
    );

    return rewardClaims.content.filter((claim) => {
      const rule = rewardRules.content.find((r) => r.id === claim.rewardRuleId);
      if (!rule) return false;
      const program = rewardPrograms.content.find(
        (p) => p.id === rule.rewardProgramId
      );
      if (!program) return false;
      return paymentConfigIds.includes(
        program.organizationPaymentConfigurationId
      );
    });
  }, [
    rewardClaims,
    adminDetails,
    cityWhitelabelMapping,
    rewardRules,
    rewardPrograms,
  ]);

  const formatAmount = (amountMinor, currency) => {
    if (!currency) {
      return `${(amountMinor / 100).toFixed(2)} (no currency)`;
    }
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amountMinor / 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return TAG_COLOR.GREEN;
      case "PENDING":
        return TAG_COLOR.ORANGE;
      case "FAILED":
        return TAG_COLOR.RED;
      case "REVERSED":
      case "EXPIRED":
        return TAG_COLOR.GREY;
      default:
        return TAG_COLOR.GREY;
    }
  };

  const truncateUuid = (uuid) => {
    if (!uuid) return "-";
    return `${uuid.substring(0, 8)}...`;
  };

  const dateFormat = (date) =>
    date ? Moment(date).format("DD.MM.YYYY HH:mm") : "-";

  const columns = useMemo(
    () => [
      {
        name: "Claim ID",
        selector: "id",
        sortable: false,
        center: true,
        cell: (row) => (
          <Tooltip title={row.id} placement="top">
            <span>{truncateUuid(row.id)}</span>
          </Tooltip>
        ),
      },
      {
        name: "User ID",
        selector: "userId",
        sortable: false,
        center: true,
        cell: (row) => (
          <Tooltip title={row.userId} placement="top">
            <span>{truncateUuid(row.userId)}</span>
          </Tooltip>
        ),
      },
      {
        name: "Rule Name",
        selector: "rewardRuleId",
        sortable: false,
        center: true,
        cell: (row) => getRuleName(row.rewardRuleId),
      },
      {
        name: "Amount",
        selector: "rewardAmountMinor",
        sortable: true,
        center: true,
        cell: (row) => formatAmount(row.rewardAmountMinor, row.currency),
      },
      {
        name: "Currency",
        selector: "currency",
        sortable: false,
        center: true,
      },
      {
        name: "Status",
        selector: "status",
        sortable: true,
        center: true,
        cell: (row) => (
          <Tag text={row.status} colorTag={getStatusColor(row.status)} />
        ),
      },
      {
        name: "Claimed At",
        selector: "claimedAt",
        sortable: true,
        center: true,
        cell: (row) => dateFormat(row.claimedAt),
      },
    ],
    [rewardRules]
  );

  const filterObject = [
    {
      filterBy: "status",
      filterValue: ["PENDING", "COMPLETED", "FAILED", "REVERSED", "EXPIRED"],
    },
  ];

  const handleDownloadCsv = () => {
    setStartDownload(true);
    setTimeout(() => {
      setStartDownload(false);
    }, 500);
  };

  const csvHeaders = [
    { label: "Claim ID", key: "id" },
    { label: "User ID", key: "userId" },
    { label: "Rule ID", key: "rewardRuleId" },
    { label: "Amount (Minor)", key: "rewardAmountMinor" },
    { label: "Currency", key: "currency" },
    { label: "Status", key: "status" },
    { label: "Claimed At", key: "claimedAt" },
  ];

  const {
    renderCustomTable,
    renderHeaderBar,
    localPage,
    localRows,
    sortData,
    debouncedSearchTerm,
    filterData,
    setLoading,
    resetPagination,
    searchFilter,
  } = useCustomTable(
    columns,
    { ...rewardClaims, content: filteredRewardClaims },
    null,
    null,
    filterObject,
    "Reward Claims",
    true,
    false
  );

  const reloadData = useCallback(() => {
    setLoading(true);
    const filterPayload = {
      page: localPage,
      size: localRows,
    };

    if (filterData && filterData.length > 0) {
      filterData.forEach((filter) => {
        if (filter.filterBy === "status") {
          filterPayload.status = filter.filter;
        }
      });
    }

    dispatch(getRewardClaims(filterPayload));
  }, [dispatch, localPage, localRows, filterData, setLoading]);

  useEffect(() => {
    if (rewardClaimsSuccess) {
      setLoading(false);
    }
  }, [rewardClaimsSuccess]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  return (
    <>
      <Box
        sx={{
          padding: "10px 0",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <Tooltip title="Download Claims CSV">
          <IconButton size="small" onClick={handleDownloadCsv}>
            <GetAppIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {startDownload && (
        <CSVDownload
          data={filteredRewardClaims}
          headers={csvHeaders}
          target="_blank"
          filename="reward-claims.csv"
        />
      )}

      {renderHeaderBar()}
      {renderCustomTable()}
    </>
  );
};

export default ClaimsContainer;
